<script setup lang="ts">
/**
 * FaqList — la liste FAQ canonique du site, charte « apple » de /dashboard
 * (Karim 2026-10-18 : « ça doit être comme dans /dashboard », sur toutes les
 * pages) : questions séparées par des filets fins, question en 21 px demi-gras,
 * rond « + » cerclé qui pivote en « × » à l'ouverture, réponse en corps de
 * lecture.
 *
 * <details>/<summary> natifs : SEO-friendly, zéro JS d'état, le navigateur
 * gère l'ouverture. Jamais de ponctuation seule en début de ligne
 * (insecables).
 *
 * Utilisé seul (articles de blog) ou via
 * FaqSectionApple qui ajoute la section et le titre à gauche.
 */
import { insecables } from "~/utils/insecables";

interface FaqItem {
  question: string;
  answer: string;
}

withDefaults(
  defineProps<{
    items: FaqItem[];
    /** Première question ouverte d'emblée (comme sur /dashboard). */
    premiereOuverte?: boolean;
  }>(),
  { premiereOuverte: false },
);

/**
 * FERMETURE ANIMÉE.
 *
 * ⚠️ Le `<details>` natif retire son attribut `open` IMMÉDIATEMENT au clic :
 * le contenu disparaît d'un coup, la transition CSS n'a pas le temps de
 * jouer. L'ouverture, elle, s'animait déjà (mesuré : 79px → 107px ; la
 * fermeture restait figée à 91px). Karim 2026-08-22.
 *
 * On intercepte donc le clic de FERMETURE, on laisse la transition se
 * dérouler, puis on retire `open` à la fin.
 *
 * ⚠️ Le `<details>` reste NATIF : pas d'état JS, l'attribut `open` est
 * toujours dans le DOM — ce qui préserve l'indexation des réponses et le
 * fonctionnement sans JavaScript.
 */
const DUREE_FERMETURE = 300; // doit rester égal à la transition CSS

function onSummaryClick(event: MouseEvent) {
  const summary = event.currentTarget as HTMLElement;
  const details = summary.parentElement as HTMLDetailsElement | null;
  if (!details?.open) return; // ouverture : le natif s'en charge très bien

  // Respecte le réglage système « animations réduites ».
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  event.preventDefault();
  details.dataset.closing = "true";
  window.setTimeout(() => {
    details.open = false;
    delete details.dataset.closing;
  }, DUREE_FERMETURE);
}
</script>

<template>
  <div class="faq-liste">
    <details
      v-for="(item, idx) in items"
      :key="item.question"
      class="faq-item group"
      :open="premiereOuverte && idx === 0"
    >
      <summary class="faq-question" @click="onSummaryClick">
        <span>{{ insecables(item.question) }}</span>
        <span class="faq-rond" aria-hidden="true">
          <UIcon name="i-lucide-plus" class="size-3.5" />
        </span>
      </summary>
      <!-- Wrapper grid : anime l'ouverture/fermeture en douceur sans JS d'état.
           grid-template-rows 0fr → 1fr est transitionnable (contrairement à
           height:auto). Le <details> natif reste (SEO + zéro JS). -->
      <div class="faq-answer-wrapper">
        <p class="faq-answer whitespace-pre-line">{{ insecables(item.answer) }}</p>
      </div>
    </details>
  </div>
</template>

<style scoped>
/* ── Charte « apple » de /dashboard : filets fins, rond cerclé ── */
.faq-liste {
  border-top: 1px solid #d2d2d7;
}
.faq-item {
  border-bottom: 1px solid #d2d2d7;
}
.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 44px;
  padding: 24px 0;
  font-size: 21px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.005em;
  color: #1d1d1f;
  list-style: none;
  cursor: pointer;
}
.faq-question::-webkit-details-marker {
  display: none;
}
.faq-rond {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 1px solid #86868b;
  border-radius: 50%;
  color: #1d1d1f;
  transition:
    transform 220ms cubic-bezier(0.28, 0, 0.22, 1),
    border-color 150ms ease,
    background-color 150ms ease;
}
@media (hover: hover) {
  .faq-question:hover .faq-rond {
    border-color: #1d1d1f;
  }
}
.faq-item[open] .faq-rond {
  transform: rotate(45deg);
}
.faq-question:focus-visible {
  outline: none;
  border-radius: 8px;
  box-shadow: 0 0 0 4px color-mix(in oklab, var(--color-main), transparent 65%);
}
@media (max-width: 640px) {
  .faq-question {
    padding: 18px 0;
    font-size: 17px;
  }
}

/* Animation d'ouverture fluide du <details> natif via grid-template-rows.
   0fr (fermé) → 1fr (ouvert) est une transition CSS valide, là où height:auto
   ne l'est pas. min-height:0 sur l'enfant permet la compression du contenu. */
.faq-answer-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

details[open] .faq-answer-wrapper {
  grid-template-rows: 1fr;
}

/* Fermeture en cours : on repasse à 0fr AVANT que `open` ne soit retiré,
   ce qui rejoue la transition en sens inverse. */
details[data-closing="true"] .faq-answer-wrapper {
  grid-template-rows: 0fr;
}
details[data-closing="true"] .faq-answer {
  margin-top: 0;
  opacity: 0;
}
/* Le rond revient en « + » dès le début de la fermeture, sans attendre. */
details[data-closing="true"] .faq-rond {
  transform: rotate(0deg);
}

.faq-answer {
  min-height: 0;
  overflow: hidden;
  /* marge haute intégrée dans la zone animée pour éviter un saut au début */
  margin-top: 0;
  opacity: 0;
  transition: opacity 0.25s ease 0.05s;
}

details[open] .faq-answer {
  opacity: 1;
}

.faq-answer {
  max-width: 68ch;
  padding: 0 52px 28px 0;
  font-size: 17px;
  line-height: 1.47;
  color: #424245;
}
@media (max-width: 640px) {
  .faq-answer {
    padding-right: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .faq-answer-wrapper,
  .faq-answer {
    transition: none;
  }
}
</style>
